import React, { useState } from "react";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
  Avatar,
  Checkbox,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogContent,
  DialogFooter,
  AreaChip,
  AreaFilter,
  TypePill,
  TypePillGroup,
  ToggleGroup,
  ToggleGroupItem,
  KanbanCard,
  KpiCardSplit,
  AreaCard,
} from "./ui-v2";
import { Monitor, Wifi, AlertCircle, Lightbulb } from "lucide-react";

/**
 * ShowcaseV2 — pantalla de demostración del Design System.
 * Renderiza todos los componentes para verificar su apariencia y validar el sistema.
 */
const ShowcaseV2 = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [check1, setCheck1] = useState(true);
  const [check2, setCheck2] = useState(false);

  return (
    <div
      className="min-h-screen bg-background p-8"
      style={{ fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
    >
      <div className="max-w-5xl mx-auto space-y-12">

        <div>
          <div className="text-xs uppercase tracking-widest text-primary font-medium mb-2">
            Design System · v1
          </div>
          <h1 className="text-3xl font-medium tracking-tight">GRUPOLAE UI</h1>
          <p className="text-muted-foreground mt-2">
            Componentes base con color corporativo #003c66 e Inter como tipografía.
          </p>
        </div>

        <section>
          <SectionTitle>Tipografía</SectionTitle>
          <Card>
            <CardContent className="pt-6 space-y-2">
              <div className="text-3xl font-medium tracking-tight">Inter · 30px · 500</div>
              <div className="text-2xl font-medium">Inter · 24px · 500</div>
              <div className="text-lg font-medium">Inter · 18px · 500</div>
              <div className="text-base">Inter · 16px · regular</div>
              <div className="text-sm text-muted-foreground">Inter · 14px · regular · muted</div>
              <div className="text-xs uppercase tracking-widest text-primary font-medium">Inter · 12px · uppercase · primary</div>
            </CardContent>
          </Card>
        </section>

        <section>
          <SectionTitle>Botones</SectionTitle>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex flex-wrap gap-3">
                <Button>Primario</Button>
                <Button variant="secondary">Secundario</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="destructive">Destructivo</Button>
                <Button variant="link">Link</Button>
              </div>
              <div className="flex flex-wrap gap-3 items-center">
                <Button size="sm">Pequeño</Button>
                <Button>Por defecto</Button>
                <Button size="lg">Grande</Button>
                <Button size="icon" aria-label="Editar">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </Button>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button disabled>Deshabilitado</Button>
                <Button variant="outline" disabled>Outline disabled</Button>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <SectionTitle>Inputs y formularios</SectionTitle>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-foreground mb-1.5 block">Email</label>
                  <Input type="email" placeholder="iban.lopetegi@grupolae.net" />
                </div>
                <div>
                  <label className="text-xs font-medium text-foreground mb-1.5 block">Rol</label>
                  <Select defaultValue="general">
                    <option value="general">General</option>
                    <option value="admin">Administrador</option>
                    <option value="superadmin">Super Administrador</option>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-foreground mb-1.5 block">Deshabilitado</label>
                <Input disabled value="No editable" />
              </div>
              <div className="flex flex-col gap-2 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={check1} onChange={e => setCheck1(e.target.checked)} />
                  <span className="text-sm">Acepto las condiciones</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={check2} onChange={e => setCheck2(e.target.checked)} />
                  <span className="text-sm">Recibir notificaciones</span>
                </label>
              </div>
            </CardContent>
          </Card>
        </section>

        <section>
          <SectionTitle>Badges</SectionTitle>
          <Card>
            <CardContent className="pt-6 flex flex-wrap gap-2">
              <Badge>Por defecto</Badge>
              <Badge variant="secondary">Secundario</Badge>
              <Badge variant="outline">Outline</Badge>
              <Badge variant="accent">Acento</Badge>
              <Badge variant="success">Activo</Badge>
              <Badge variant="warning">Pendiente</Badge>
              <Badge variant="destructive">Baja</Badge>
            </CardContent>
          </Card>
        </section>

        <section>
          <SectionTitle>Avatares</SectionTitle>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <Avatar name="Iban Lopetegi" size="xs" />
                <Avatar name="María López" size="sm" />
                <Avatar name="Alaitz García" />
                <Avatar name="Adrian Senin" size="lg" />
                <Avatar name="Águeda López Baño" size="xl" />
              </div>
              <p className="text-sm text-muted-foreground">
                Los colores son estables: el mismo nombre siempre produce el mismo color.
              </p>
            </CardContent>
          </Card>
        </section>

        <section>
          <SectionTitle>Tarjetas (KPI)</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Llamadas totales</CardDescription>
                <CardTitle className="text-2xl">70.895</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xs text-emerald-600">↑ 12% vs mes anterior</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Atendidas</CardDescription>
                <CardTitle className="text-2xl">50.694</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xs text-muted-foreground">71,5% del total</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Sin etiquetar</CardDescription>
                <CardTitle className="text-2xl">20.201</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge variant="warning">28,5% del total</Badge>
              </CardContent>
            </Card>
          </div>
        </section>

        <section>
          <SectionTitle>Diálogo (modal)</SectionTitle>
          <Card>
            <CardContent className="pt-6">
              <Button onClick={() => setDialogOpen(true)}>Abrir diálogo</Button>
            </CardContent>
          </Card>
          <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} size="default">
            <DialogHeader>
              <DialogTitle>Importar datos de RingOver</DialogTitle>
              <DialogDescription>
                Sube un CSV mensual para añadirlo a las estadísticas.
              </DialogDescription>
            </DialogHeader>
            <DialogContent>
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary hover:bg-accent/30 transition-colors cursor-pointer">
                <p className="text-sm text-muted-foreground">Arrastra el CSV aquí o haz click para seleccionar</p>
              </div>
            </DialogContent>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={() => setDialogOpen(false)}>Importar</Button>
            </DialogFooter>
          </Dialog>
        </section>

        {/* ===== DS v2.5 · Componentes funcionales ===== */}
        <section>
          <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">
            Design System · v2.5
          </div>
          <h2 className="text-2xl font-semibold tracking-tight mb-6">Componentes funcionales</h2>

          <SectionTitle>AreaChip</SectionTitle>
          <div className="flex gap-2 flex-wrap mb-8">
            <AreaChip area="intranet">Intranet</AreaChip>
            <AreaChip area="hardware">Hardware</AreaChip>
            <AreaChip area="software">Software</AreaChip>
            <AreaChip area="accesos">Accesos</AreaChip>
            <AreaChip area="conectividad">Conectividad</AreaChip>
            <AreaChip area="instalaciones">Instalaciones</AreaChip>
            <AreaChip area="rrhh">RRHH</AreaChip>
            <AreaChip area="otros">Otros</AreaChip>
          </div>

          <SectionTitle>AreaFilter</SectionTitle>
          <div className="flex gap-2 flex-wrap mb-8">
            <AreaFilter active={true} count={42}>Todas</AreaFilter>
            <AreaFilter active={false} count={12}>Hardware</AreaFilter>
            <AreaFilter active={false} count={8}>Software</AreaFilter>
          </div>

          <SectionTitle>TypePillGroup</SectionTitle>
          <div className="mb-8">
            <TypePillGroup>
              <TypePill active={true} icon={<AlertCircle size={16} />} label="Incidencias" count={12} />
              <TypePill active={false} icon={<Lightbulb size={16} />} label="Sugerencias" count={5} />
            </TypePillGroup>
          </div>

          <SectionTitle>ToggleGroup</SectionTitle>
          <div className="mb-8">
            <ToggleGroup>
              <ToggleGroupItem active={true}>Tabla</ToggleGroupItem>
              <ToggleGroupItem active={false}>Kanban</ToggleGroupItem>
            </ToggleGroup>
          </div>

          <SectionTitle>KanbanCard</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mb-8">
            <KanbanCard priority="alta" aging="warning" id="INC-042" title="VPN no conecta desde la oficina" meta="Carlos R. · hace 4d" />
            <KanbanCard priority="critica" aging="danger" id="INC-045" title="No puedo acceder al ERP" meta="Ana G. · hace 8d" />
            <KanbanCard priority="baja" aging="none" id="INC-051" title="Cambiar fondo de pantalla corporativo" meta="Pedro M. · hoy" />
          </div>

          <SectionTitle>KpiCardSplit</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl mb-8">
            <KpiCardSplit label="Incidencias abiertas" value="24" topBorderClass="border-t-area-hardware-fg" splitA={{ label: "Críticas", value: "3" }} splitB={{ label: "Pendientes", value: "21" }} />
            <KpiCardSplit label="Software" value="9" topBorderClass="border-t-area-software-fg" splitA={{ label: "En curso", value: "4" }} splitB={{ label: "Resueltas", value: "5" }} />
          </div>

          <SectionTitle>AreaCard</SectionTitle>
          <div className="flex gap-2 flex-wrap">
            <AreaCard icon={<Monitor size={20} />} label="Hardware" area="hardware" selected={true} />
            <AreaCard icon={<Wifi size={20} />} label="Red" area="conectividad" selected={false} />
          </div>
        </section>

      </div>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <h2 className="text-xs uppercase tracking-widest text-muted-foreground font-medium mb-3">
    {children}
  </h2>
);

export default ShowcaseV2;
